import React, { useEffect, useState } from 'react';
import Lottie from 'lottie-react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';

// Import animation data statically
import animationDataStatic from '../assets/animations/bez-saules.json';

interface ProcessingStep {
  id: string;
  labelKey: string;
  status: 'pending' | 'active' | 'completed';
}

interface SymptomProcessingOverlayProps {
  isVisible: boolean;
  currentStep: string;
  hasImages?: boolean;
  mode?: 'initial' | 'summary'; // 'initial' for symptom submission, 'summary' for summary generation
}

const SymptomProcessingOverlay: React.FC<SymptomProcessingOverlayProps> = ({
  isVisible,
  currentStep,
  hasImages = false,
  mode = 'initial'
}) => {
  const { t } = useI18n();
  const [steps, setSteps] = useState<ProcessingStep[]>([]);
  const [animationData, setAnimationData] = useState<any>(null);
  const [animationError, setAnimationError] = useState(false);
  const [isLoadingAnimation, setIsLoadingAnimation] = useState(false);

  // Set animation data from static import
  useEffect(() => {
    if (animationData) return;
    
    // Validate it's a Lottie animation
    if (animationDataStatic && (!animationDataStatic.v && !animationDataStatic.fr)) {
      console.warn('Loaded file does not appear to be a valid Lottie animation', animationDataStatic);
      setAnimationError(true);
    } else if (animationDataStatic) {
      console.log('Lottie animation loaded successfully', { v: animationDataStatic.v, fr: animationDataStatic.fr });
      setAnimationData(animationDataStatic);
      setAnimationError(false);
    }
  }, []);

  useEffect(() => {
    let initialSteps: ProcessingStep[] = [];
    
    if (mode === 'summary') {
      // Steps for summary generation
      initialSteps = [
        { id: 'readingAnswers', labelKey: 'logSymptom.processing.readingAnswers', status: 'pending' },
        { id: 'analyzing', labelKey: 'logSymptom.processing.analyzing', status: 'pending' },
        { id: 'preparingSummary', labelKey: 'logSymptom.processing.preparingSummary', status: 'pending' }
      ];
    } else {
      // Steps for initial symptom submission
      initialSteps = [
        { id: 'reading', labelKey: 'logSymptom.processing.reading', status: 'pending' },
        ...(hasImages ? [{ id: 'checking', labelKey: 'logSymptom.processing.checkingPhotos', status: 'pending' } as ProcessingStep] : []),
        { id: 'analyzing', labelKey: 'logSymptom.processing.analyzing', status: 'pending' },
        { id: 'preparing', labelKey: 'logSymptom.processing.preparingQuestions', status: 'pending' }
      ];
    }
    
    setSteps(initialSteps);
  }, [hasImages, mode]);

  useEffect(() => {
    if (!isVisible) return;

    setSteps(prevSteps => {
      const stepIndex = prevSteps.findIndex(s => s.id === currentStep);
      if (stepIndex === -1) return prevSteps;

      return prevSteps.map((step, index) => {
        if (index < stepIndex) {
          return { ...step, status: 'completed' as const };
        } else if (index === stepIndex) {
          return { ...step, status: 'active' as const };
        } else {
          return { ...step, status: 'pending' as const };
        }
      });
    });
  }, [currentStep, isVisible]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
        {/* Lottie Animation */}
        <div className="flex justify-center mb-6">
          <div className="w-48 h-48 relative">
            {isLoadingAnimation ? (
              <div className="w-full h-full flex items-center justify-center">
                <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />
              </div>
            ) : animationData && !animationError ? (
              <Lottie
                animationData={animationData}
                loop={true}
                autoplay={true}
                style={{ width: '100%', height: '100%' }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />
              </div>
            )}
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-4">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center gap-3">
              {/* Status Icon */}
              <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                {step.status === 'completed' && (
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                )}
                {step.status === 'active' && (
                  <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                )}
                {step.status === 'pending' && (
                  <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                )}
              </div>

              {/* Step Label */}
              <span
                className={`text-sm font-medium transition-colors ${
                  step.status === 'completed'
                    ? 'text-green-600'
                    : step.status === 'active'
                    ? 'text-blue-600'
                    : 'text-gray-400'
                }`}
              >
                {t(step.labelKey) || step.labelKey}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SymptomProcessingOverlay;

